import { useMemo } from 'react';
import { useFlags } from '@fullstory/react-hooks';
import { PageDefinition } from '@fullstory/proto/fs/api/pages';
import { getMetricForEvent } from '@fullstory/signal-studio-context';
import { useMetricComputations } from '@fullstory/library-ui';
import { hashObject } from '@fullstory/util/hash';
import { buildTrendPoint } from '@fullstory/visualizations';
import { isNonNull } from '@fullstory/typeutils';
import { DisplayPrecision } from '@fullstory/proto/fs/services/webber/frontend/display';
import { makePageMultiPatternDeps } from '../utils/signal-studio';

// TODO(nate): This duplicates useMetricsTrendData logic from named-elements-ui. That package
// imports pages-shared, though, so we'd need to extract useMetricsTrendData to a shared package
// to reference from both.
export const usePageSuggestionTrendData = (
  pageDefinition: PageDefinition,
  timeRange: { startTime: string; endTime: string },
  shouldFetch: boolean,
) => {
  const flags = useFlags();

  const event = useMemo(() => {
    if (!shouldFetch || !pageDefinition.suggestion || !pageDefinition.patterns?.length) {
      return null;
    }

    const deps = makePageMultiPatternDeps(pageDefinition.patterns, flags);

    return {
      primary: { visitedPage: { any: {} } },
      deps, // Page pattern dependencies
    };
  }, [pageDefinition.suggestion, pageDefinition.patterns, flags, shouldFetch]);

  const eventId = useMemo(() => hashObject(event), [event]);

  const metric = useMemo(() => {
    if (!event) {
      return null;
    }

    return getMetricForEvent({ event, timeRange });
  }, [event, timeRange]);

  const { responses } = useMetricComputations(metric, !!metric, `trend-metric-${eventId}`);

  const trendPoints = useMemo(() => {
    const computeMetricResponses = responses.flatMap(r => r.data);

    const current = computeMetricResponses
      .filter(r => !r?.isPast)
      .map(r => r?.data)
      .filter(isNonNull);

    return current.flatMap((data, parentIndex) =>
      data.table
        .map((dataItem, i) =>
          buildTrendPoint(
            dataItem,
            parentIndex,
            i,
            { displayPrecision: DisplayPrecision.PRECISION_0 },
            null,
          ),
        )
        .filter(isNonNull),
    );
  }, [responses]);

  return { trendPoints };
};
